import * as React from 'react';

import { useIntl } from 'react-intl';

import {
    SidePanelBodyAttribute,
    SidePanelBodyAttributeBody,
    SidePanelBodyAttributeTitle,
} from 'src/components/sidePanel/SidePanelBody';
import { useGetInParkingCard } from 'src/features/cards/api/useGetInParkingCard';
import CardModalFormButtons from 'src/features/cards/components/CardModalFormButtons.tsx';
import useCalculateStayTime from 'src/features/cards/hooks/useCalculateStayTime';
import { type InParkingCard } from 'src/features/cards/types/card';
import { badgeCardTypeOptions } from 'src/features/cards/utils/constants';
import { cardMessages } from 'src/features/cards/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import { Badge } from 'src/registry/ui/Badge';
import { Button } from 'src/registry/ui/Button';
import {
    DialogBody,
    DialogClose,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
} from 'src/registry/ui/Dialog';
import ImageModal from 'src/registry/ui/ImageModal';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

type CardInParkingDetailProps = {
    inParkingCard?: InParkingCard;
    refetch: () => void;
};

//TODO: hardcoded currency, change once it can be dynamic

export function CardInParkingDetail(props: CardInParkingDetailProps) {
    const { inParkingCard, refetch: tableRefetch } = props;

    const [stayTime, setStayTime] = React.useState('');

    const { parkingId } = useParking();
    const { calculateStayTime } = useCalculateStayTime();
    const { formatMessage, formatNumber } = useIntl();

    const {
        data: cardData,
        isLoading,
        error: cardError,
        refetch: cardRefetch,
    } = useGetInParkingCard({
        parkingId,
        id: inParkingCard?.id,
        config: { enabled: !!inParkingCard?.id },
    });

    const cardNumber = cardData?.card_number ? cardData?.card_number : inParkingCard?.card_number;

    React.useEffect(() => {
        const stayString = calculateStayTime(cardData ? cardData : inParkingCard);
        setStayTime(stayString);

        const interval = setInterval(() => {
            const stayString = calculateStayTime(cardData);
            setStayTime(stayString);
        }, 60 * 1000); // Update every minute

        return () => clearInterval(interval);
    }, [calculateStayTime, cardData, inParkingCard]);

    const refetch = () => {
        tableRefetch();
        void cardRefetch();
    };

    const photoPath = cardData?.photo_path ? cardData?.photo_path : inParkingCard?.photo_path;
    const errorData = cardError?.response?.data;

    const errorMessageTranslationMap = {
        'Charge plan for late not found!': formatMessage(generalMessages.chargePlanForLateNotFound),
        'Something went wrong when trying to reach the Parking': formatMessage(generalMessages.errorConnectToParking),
    };

    const translatedErrorMessage =
        errorMessageTranslationMap[errorData?.message as keyof typeof errorMessageTranslationMap] ??
        formatMessage(generalErrorMessages.unknownError);

    return (
        <>
            {inParkingCard ? (
                <DialogHeader>
                    <div className="flex items-center gap-2">
                        <DialogTitle className="pr-0 font-mono">{cardNumber}</DialogTitle>
                        <Badge theme={badgeCardTypeOptions[inParkingCard.card_type].theme}>
                            {formatMessage(badgeCardTypeOptions[inParkingCard.card_type].label)}
                        </Badge>
                    </div>
                    <DialogDescription>{inParkingCard.entry_point}</DialogDescription>
                </DialogHeader>
            ) : (
                <DialogHeader>
                    <Skeleton className="h-7 w-32" />
                    <Skeleton className="h-5 w-60" />
                </DialogHeader>
            )}

            <DialogSeparator />

            <DialogBody>
                <ImageModal
                    image={`/parkings/images/${parkingId}/${photoPath}`}
                    alt={inParkingCard?.card_number}
                    title={cardNumber}
                    disableClick={!photoPath}
                />

                <CardModalFormButtons data={cardData} refetch={refetch} cardInParkingData={inParkingCard} />

                {cardError && <Badge theme="red">{translatedErrorMessage}</Badge>}
                <div className="mt-4 grid gap-2">
                    <SidePanelBodyAttribute>
                        <SidePanelBodyAttributeTitle>
                            {formatMessage(cardMessages.cardNumber)}
                        </SidePanelBodyAttributeTitle>
                        <SidePanelBodyAttributeBody
                            control={cardData?.card_number || inParkingCard?.card_number}
                            fallbackMessage={formatMessage(generalMessages.noValue)}
                        >
                            {cardNumber}
                        </SidePanelBodyAttributeBody>
                    </SidePanelBodyAttribute>

                    <SidePanelBodyAttribute>
                        <SidePanelBodyAttributeTitle>
                            {formatMessage(cardMessages.parkingZone)}
                        </SidePanelBodyAttributeTitle>
                        <SidePanelBodyAttributeBody
                            control={inParkingCard?.parking_name}
                            fallbackMessage={formatMessage(generalMessages.noValue)}
                        >
                            {inParkingCard?.parking_name}
                        </SidePanelBodyAttributeBody>
                    </SidePanelBodyAttribute>

                    <SidePanelBodyAttribute>
                        <SidePanelBodyAttributeTitle>
                            {formatMessage(cardMessages.currentStay)}
                        </SidePanelBodyAttributeTitle>
                        <SidePanelBodyAttributeBody
                            control={stayTime}
                            fallbackMessage={formatMessage(generalMessages.noValue)}
                        >
                            {stayTime}
                        </SidePanelBodyAttributeBody>
                    </SidePanelBodyAttribute>

                    <SidePanelBodyAttribute>
                        <SidePanelBodyAttributeTitle>
                            {formatMessage(cardMessages.chargePlan)}
                        </SidePanelBodyAttributeTitle>
                        <SidePanelBodyAttributeBody
                            control={inParkingCard?.charge_plan_name}
                            fallbackMessage={formatMessage(generalMessages.noValue)}
                        >
                            {inParkingCard?.charge_plan_name}
                        </SidePanelBodyAttributeBody>
                    </SidePanelBodyAttribute>

                    <SidePanelBodyAttribute>
                        <SidePanelBodyAttributeTitle>
                            {formatMessage(cardMessages.dueAmount)}
                        </SidePanelBodyAttributeTitle>
                        <SidePanelBodyAttributeBody
                            control={!isLoading && cardData?.due_amount}
                            fallbackMessage={formatMessage(generalMessages.noValue)}
                        >
                            {cardData &&
                                formatNumber(cardData.due_amount / 100, {
                                    style: 'currency',
                                    currency: 'BGN',
                                })}
                        </SidePanelBodyAttributeBody>
                    </SidePanelBodyAttribute>

                    <SidePanelBodyAttribute>
                        <SidePanelBodyAttributeTitle>
                            {formatMessage(cardMessages.taxAmount)}
                        </SidePanelBodyAttributeTitle>
                        <SidePanelBodyAttributeBody
                            control={!isLoading && cardData?.tax_amount}
                            fallbackMessage={formatMessage(generalMessages.noValue)}
                        >
                            {cardData &&
                                formatNumber(cardData.tax_amount / 100, {
                                    style: 'currency',
                                    currency: 'BGN',
                                })}
                        </SidePanelBodyAttributeBody>
                    </SidePanelBodyAttribute>

                    <SidePanelBodyAttribute>
                        <SidePanelBodyAttributeTitle>
                            {formatMessage(cardMessages.totalDue)}
                        </SidePanelBodyAttributeTitle>
                        <SidePanelBodyAttributeBody
                            control={!isLoading && cardData?.due_amount_with_tax}
                            fallbackMessage={formatMessage(generalMessages.noValue)}
                        >
                            {cardData &&
                                formatNumber(cardData.due_amount_with_tax / 100, {
                                    style: 'currency',
                                    currency: 'BGN',
                                })}
                        </SidePanelBodyAttributeBody>
                    </SidePanelBodyAttribute>
                </div>
            </DialogBody>

            <DialogSeparator />

            <DialogFooter>
                <DialogClose asChild>
                    <Button variant="outline" className="flex-1">
                        {formatMessage(generalMessages.close)}
                    </Button>
                </DialogClose>
            </DialogFooter>
        </>
    );
}
